import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useSeo } from "@/hooks/useSeo";
import {
  allArticles,
  categories,
  categoriesMeta,
  homeFaqs,
} from "@/mocks/ranking";
import HomeHero from "./components/Hero";
import { ArticleCard, RankBadge, SectionHeader, Tag } from "@/components/feature/cards";

const annual = allArticles.filter((a) => a.group === "annual");
const monthly = allArticles.filter((a) => a.group === "monthly");
const featuredAnnual = annual[0];
const heatTop = allArticles.slice(0, 5);

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg bg-background-50 ring-1 ring-background-200/70">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left md:px-5"
      >
        <span className="text-sm font-semibold text-foreground-950 md:text-base">{q}</span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 transition-transform ${
            open ? "rotate-45" : ""
          }`}
        >
          <i className="ri-add-line" aria-hidden="true"></i>
        </span>
      </button>
      {open && (
        <div className="border-t border-background-200/70 px-4 pb-4 pt-3 md:px-5">
          <p className="text-sm leading-6 text-foreground-600">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  useSeo({
    title: "吃瓜榜单 - 年度盘点与月度总结，吃瓜不断档",
    description:
      "吃瓜榜单一站式回顾年度盘点与月度总结，从年度瓜王到月度黑马一站看全，帮你把错过的瓜都补上，吃瓜不断档。",
    keywords: "吃瓜榜单,年度盘点,月度总结,瓜王",
    canonicalPath: "/",
    ogType: "website",
    jsonLdId: "home",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "吃瓜榜单",
          description: "热点年度盘点与月度总结的一站式榜单站",
          url: "https://chiguabangdan.example/",
        },
        {
          "@type": "WebSite",
          name: "吃瓜榜单",
          url: "https://chiguabangdan.example/",
          description: "吃瓜榜单年度盘点、月度总结、瓜王盘点",
        },
        {
          "@type": "FAQPage",
          mainEntity: homeFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    },
  });

  return (
    <Layout>
      <HomeHero />

      {/* 栏目分类 */}
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="mt-[-40px] relative z-20 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => {
            const meta = categoriesMeta[c.name];
            return (
              <Link
                key={c.name}
                to="/annual"
                className="group flex items-center gap-4 rounded-lg bg-background-100/95 p-4 ring-1 ring-background-200/80 backdrop-blur transition-all hover:-translate-y-1 hover:bg-primary-100/70"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={`${meta.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-950">{c.name}</span>
                  <span className="mt-0.5 block text-xs text-foreground-600">{c.desc}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 热力榜 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          eyebrow="Trending"
          title="本周吃瓜热力榜"
          desc="按全网讨论热度实时滚动，热度越高的瓜越要靠前吃。"
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {heatTop.slice(0, 4).map((a) => (
            <Link
              key={a.id}
              to={`/rank/${a.id}`}
              className="group flex items-center gap-4 rounded-lg bg-background-100/70 p-4 ring-1 ring-background-200/70 transition-all hover:bg-background-100 hover:ring-primary-300"
            >
              <RankBadge rank={a.rank} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-foreground-950 group-hover:text-primary-700 md:text-base">
                  {a.title}
                </span>
                <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                  {a.heat}
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline">{a.category}</span>
                </span>
              </span>
              <i className="ri-arrow-right-line text-foreground-300 transition-all group-hover:translate-x-1 group-hover:text-primary-500" aria-hidden="true"></i>
            </Link>
          ))}
        </div>
      </section>

      {/* 年度盘点精选 */}
      <section className="bg-background-100/60 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeader
            eyebrow="Annual Review"
            title="年度盘点精选"
            desc="一年一度的重头戏，把年度瓜王与各领域大事件一次讲清楚。"
            to="/annual"
            linkLabel="全部年度盘点"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Link
              to={`/rank/${featuredAnnual.id}`}
              className="group relative flex min-h-[360px] flex-col justify-end overflow-hidden rounded-lg"
            >
              <img
                src={featuredAnnual.cover}
                alt={`${featuredAnnual.title}封面`}
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-transparent"></div>
              <div className="relative z-10 p-6 md:p-7">
                <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
                  <i className="ri-trophy-fill" aria-hidden="true"></i>
                  年度瓜王候选
                </span>
                <h3 className="mt-3 text-xl font-black leading-8 text-background-50 md:text-2xl">
                  {featuredAnnual.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm text-background-100">{featuredAnnual.excerpt}</p>
              </div>
            </Link>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {annual.slice(1, 3).map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 月度总结 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeader
          eyebrow="Monthly Roundup"
          title="月度总结 · 本月瓜田"
          desc="每月初更新，把上个月的瓜按热度与后劲排出座次，月度黑马也不错过。"
          to="/monthly"
          linkLabel="全部月度总结"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {monthly.slice(0, 3).map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      </section>

      {/* 瓜王 & 黑马 编辑条 */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="relative flex min-h-[260px] items-end overflow-hidden rounded-lg bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 p-7">
            <div className="absolute inset-0 opacity-20">
              <img src={featuredAnnual.cover} alt="" className="h-full w-full object-cover object-top" />
            </div>
            <div className="relative z-10">
              <p className="font-display text-lg text-background-50">年度瓜王 · 镇场担当</p>
              <h3 className="mt-1 text-2xl font-black leading-9 text-background-50">{featuredAnnual.title}</h3>
              <Link
                to={`/rank/${featuredAnnual.id}`}
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-accent-100"
              >
                看它凭什么封王
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
          <div className="flex flex-col justify-center gap-4 rounded-lg bg-background-100/70 p-7 ring-1 ring-background-200/70">
            <p className="font-display text-lg text-primary-600">月度黑马 · 本月潜力股</p>
            <p className="text-sm leading-6 text-foreground-600">
              每一期的月度总结里，我们都会单独标记一匹"开局平平、口碑逆袭"的黑马，帮你提前锁定那些值得追的潜力内容，而不是等它爆了才发现错过了前几集。
            </p>
            <Link
              to="/monthly"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
              style={{ width: "fit-content" }}
            >
              去认识本月黑马
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100/60 py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          <SectionHeader eyebrow="FAQ" title="关于吃瓜榜单，你可能想问" />
          <div className="flex flex-col gap-3">
            {homeFaqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* 投稿 CTA */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col items-start gap-6 rounded-lg bg-primary-100/70 p-7 md:flex-row md:items-center md:justify-between md:p-10">
          <div className="max-w-2xl">
            <h2 className="flex items-center gap-2 text-2xl font-black text-foreground-950">
              <i className="ri-quill-pen-line text-primary-600" aria-hidden="true"></i>
              有瓜没处说？来投稿线索
            </h2>
            <p className="mt-2 text-sm leading-6 text-foreground-700">
              如果你有希望被认真盘点的热点线索，欢迎告诉我们。我们会优先评估话题的公共价值与可信度，审核通过后纳入后续盘点选题。
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["线索真实", "持续跟进", "客观盘点"].map((t) => (
                <Tag key={t} label={t} />
              ))}
            </div>
          </div>
          <Link
            to="/contact"
            className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3.5 font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            我要投稿瓜线索
          </Link>
        </div>
      </section>
    </Layout>
  );
}